import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  TextField,
  InputAdornment,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import {
  DataGrid,
  Column,
  Paging,
  Selection,
} from "devextreme-react/data-grid";
import { useMemo, useState, useCallback, useRef, useEffect } from "react";

import Tooltip from "@mui/material/Tooltip";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";

import "./RestoreArchivedFilesModal.scss";
import { dataGridConfig } from "../../../constants";

// Constants for validation limits
const MAX_FILE_COUNT = 5;
const MAX_TOTAL_SIZE_BYTES = 2 * 1024 * 1024 * 1024; // 2 GB in bytes

interface RestoreArchivedFilesModalProps {
  open: boolean;
  onClose: () => void;
  archivedFiles: any[]; // Replace with a proper type
  onRestore: (selectedFiles: any[]) => Promise<void>;
}

export const RestoreArchivedFilesModal = ({
  open,
  onClose,
  archivedFiles,
  onRestore,
}: RestoreArchivedFilesModalProps) => {
  const dataGridRef = useRef<DataGrid>(null);
  const [selectedFiles, setSelectedFiles] = useState<any[]>([]);
  const [selectedZipName, setSelectedZipName] = useState<string | null>(null);
  const selectedFilesRef = useRef<any[]>([]);
  const selectedZipNameRef = useRef<string | null>(null);
  const [searchText, setSearchText] = useState("");
  const [isRestoring, setIsRestoring] = useState(false);
  const inputRef = useRef<HTMLInputElement | null>(null);

  // Clear search box when modal opens (and on initial component mount when open may be true on load)
  useEffect(() => {
    if (open) {
      setSearchText("");
    }
  }, [open]);

  // Ensure search is cleared on every page load / component mount
  useEffect(() => {
    setSearchText("");
  }, []);

  /**
   * Resets the selection state of the modal.
   */
  const resetSelection = useCallback(() => {
    selectedFilesRef.current = [];
    selectedZipNameRef.current = null;
    setSelectedFiles([]);
    setSelectedZipName(null);
    if (dataGridRef.current) {
      try {
        dataGridRef.current.instance.clearSelection();
      } catch (err) {
        // ignore
      }
    }
  }, []);

  useEffect(() => {
    // When the archived files list changes (refresh after restore), clear selection state
    // to avoid stale selection referencing old objects.
    resetSelection();

    // First, remove any lingering disabled classes from the modal root as a broad fallback.
    try {
      const modalRoot = document.querySelector('.restore-archived-files-modal');
      if (modalRoot && typeof modalRoot.querySelectorAll === 'function') {
        modalRoot.querySelectorAll('.disabled-row').forEach((n) => n.classList.remove('disabled-row'));
      }
    } catch (err) {
      // ignore
    }

    // After resetting selection, remove any lingering 'disabled-row' classes from the grid DOM
    // so rows are re-enabled, then force a repaint/refresh so onRowPrepared runs for visible rows.
    if (dataGridRef.current) {
      try {
        const instance: any = (dataGridRef.current as any).instance;

        try {
          // Get the root element whether instance.element is a function or a direct property
          // @ts-ignore
          let rootEl: any = null;
          try {
            if (typeof instance.element === "function") {
              // element() returns a wrapper or DOM element
              rootEl = instance.element();
            } else {
              rootEl = instance.element;
            }
          } catch (innerErr) {
            // ignore
            rootEl = null;
          }

          // Normalize jQuery-like wrapper to DOM element
          const domEl = rootEl && rootEl[0] ? rootEl[0] : rootEl;

          const removeDisabled = () => {
            try {
              if (domEl && typeof domEl.querySelectorAll === "function") {
                const disabledNodes = domEl.querySelectorAll(".disabled-row");
                disabledNodes.forEach((n: Element) => n.classList.remove("disabled-row"));
              }
            } catch (err) {
              // ignore
            }
          };

          // Run immediately and again on next tick to cover any virtualized rows that render slightly later
          removeDisabled();
          setTimeout(removeDisabled, 50);
        } catch (err) {
          // ignore DOM cleanup errors
        }

        try {
          // repaintRows is preferred to avoid a full refresh; fall back to repaint/refresh
          if (typeof instance.repaintRows === "function") {
            instance.repaintRows();
          } else if (typeof instance.repaint === "function") {
            instance.repaint();
          } else if (typeof instance.refresh === "function") {
            instance.refresh();
          }
        } catch (err) {
          try {
            if (typeof instance.refresh === "function") {
              instance.refresh();
            }
          } catch (e) {
            // ignore
          }
        }
      } catch (err) {
        // ignore any grid instance errors
      }
    }
  }, [archivedFiles, resetSelection]);

  const formatSelectedFileSize = (bytes: number) => {
    if (!bytes) {
      return "0.00";
    }

    if (bytes >= 1024 * 1024 * 1024) {
      return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`;
    }

    if (bytes >= 1024 * 1024) {
      return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
    }

    return `${(bytes / 1024).toFixed(2)} KB`;
  };

  const selectedFileInfo = useMemo(() => {
    let totalSizeBytes = 0;
    let hasAnySizeInfo = false;

    selectedFiles.forEach((file) => {
      const fileSize = Number(file?.size);

      if (!isNaN(fileSize) && fileSize > 0) {
        totalSizeBytes += fileSize;
        hasAnySizeInfo = true;
      }
    });

    return {
      count: selectedFiles.length,
      totalSizeBytes,
      hasAnySizeInfo,
      formattedSize: formatSelectedFileSize(totalSizeBytes),
    };
  }, [selectedFiles]);

  const filteredArchivedFiles = useMemo(() => {
    const search = searchText.trim().toLowerCase();

    // Show all rows if less than 3 characters
    if (search.length < 3) {
      return archivedFiles;
    }

    return archivedFiles.filter((file) => {
      return (
        file.fileName?.toLowerCase().includes(search) ||
        file.folder?.toLowerCase().includes(search) ||
        file.zip?.toLowerCase().includes(search)
      );
    });
  }, [archivedFiles, searchText]);

  // Determine if restore button should be enabled
  const isRestoreEnabled = useMemo(() => {
    const { count, totalSizeBytes, hasAnySizeInfo } = selectedFileInfo;

    // Must have at least 1 file selected
    if (count === 0) {
      return false;
    }

    // File count must not exceed MAX_FILE_COUNT
    if (count > MAX_FILE_COUNT) {
      return false;
    }

    // If size info is available, validate total size does not exceed limit
    if (hasAnySizeInfo && totalSizeBytes > MAX_TOTAL_SIZE_BYTES) {
      return false;
    }

    return true;
  }, [selectedFileInfo]);

  // Handle selection changes from the DataGrid, but exclude already-restored rows.
  const handleSelectionChanged = useCallback((e: any) => {
    const selectedRows = e.selectedRowsData || [];

    if (selectedRows.length === 0) {
      selectedFilesRef.current = [];
      selectedZipNameRef.current = null;
      setSelectedFiles([]);
      setSelectedZipName(null);
      return;
    }

    // Maintain the same-zip selection rule
    const firstZip = selectedRows[0]?.zip;
    const filteredRows = selectedRows.filter(
      (row: any) => row.zip === firstZip,
    );

    const currentSelectedKeys = new Set(e.currentSelectedRowKeys || []);
    const excessCount = Math.max(0, filteredRows.length - MAX_FILE_COUNT);
    const newlySelectedRows = filteredRows.filter((row: any) =>
      currentSelectedKeys.has(row.id),
    );
    const excessRows = excessCount > 0
      ? newlySelectedRows.slice(-excessCount)
      : [];

    if (excessRows.length < excessCount) {
      const excessRowKeys = new Set(excessRows.map((row: any) => row.id));
      filteredRows
        .filter((row: any) => !excessRowKeys.has(row.id))
        .slice(-(excessCount - excessRows.length))
        .forEach((row: any) => excessRows.push(row));
    }

    const rejectedRowKeys = new Set(excessRows.map((row: any) => row.id));
    const validRows = filteredRows.filter(
      (row: any) => !rejectedRowKeys.has(row.id),
    );
    const differentZipRows = selectedRows.filter(
      (row: any) => row.zip !== firstZip,
    );

    selectedFilesRef.current = validRows;
    selectedZipNameRef.current = firstZip;
    setSelectedFiles(validRows);
    setSelectedZipName(firstZip);

    const rowsToDeselect = [...differentZipRows, ...excessRows];
    if (rowsToDeselect.length > 0) {
      try {
        e.component.deselectRows(
          rowsToDeselect.map((row: any) => row.id),
        );
      } catch (err) {}
    }
  }, []);

  const updateRowState = useCallback((rowElement: any, rowData: any) => {
    const currentSelectedFiles = selectedFilesRef.current;
    const currentSelectedZipName = selectedZipNameRef.current;
    const isSelected = currentSelectedFiles.some(
      (file: any) => file.id === rowData?.id,
    );
    const isDisabledBySelectionLimit =
      currentSelectedFiles.length >= MAX_FILE_COUNT && !isSelected;

    rowElement.classList.toggle(
      "disabled-row",
      Boolean(currentSelectedZipName && rowData?.zip !== currentSelectedZipName),
    );

    rowElement.classList.toggle(
      "selection-limit-row",
      isDisabledBySelectionLimit,
    );

    const selectionCheckbox = rowElement.querySelector(
      ".dx-command-select .dx-checkbox",
    );
    if (selectionCheckbox) {
      selectionCheckbox.classList.toggle(
        "dx-state-disabled",
        isDisabledBySelectionLimit,
      );
      selectionCheckbox.setAttribute(
        "aria-disabled",
        String(isDisabledBySelectionLimit),
      );
    }
  }, []);

  const onRowPrepared = useCallback((e: any) => {
    if (e.rowType === "data") {
      updateRowState(e.rowElement, e.data);
    }
  }, [updateRowState]);

  useEffect(() => {
    try {
      const instance: any = dataGridRef.current?.instance;
      const visibleRows = instance?.getVisibleRows?.() || [];

      visibleRows.forEach((row: any) => {
        if (row.rowType !== "data") {
          return;
        }

        const rowElementResult = instance.getRowElement(row.rowIndex);
        const rowElement = rowElementResult?.[0] || rowElementResult;
        if (rowElement) {
          updateRowState(rowElement, row.data);
        }
      });
    } catch (err) {
      // ignore
    }
  }, [selectedFiles, selectedZipName, updateRowState]);

  const handleRestoreClick = async () => {
    // Prevent multiple clicks
    if (isRestoring) {
      return;
    }
    setIsRestoring(true);
    try {
      await onRestore(selectedFiles);
    } catch (err) {
      console.error("Error while calling onRestore:", err);
    } finally {
      // Reset selection so user can make a fresh selection after a restore
      resetSelection();
      // Enable button again after API completes
      setIsRestoring(false);
    }
  };

  // Render function for the selection column header to show the "Select" text
  const renderSelectionHeader = useCallback(() => {
    return <div className="dx-selection-header">Select</div>;
  }, []);

  // Handler to clear the search box and restore focus
  const handleClearSearch = useCallback(() => {
    setSearchText("");
    // focus the input element after clearing
    setTimeout(() => {
      try {
        inputRef.current?.focus();
      } catch (err) {}
    }, 0);
  }, []);

  const archivedFilesGrid = useMemo(() => (
    <DataGrid
      ref={dataGridRef}
      dataSource={filteredArchivedFiles}
      onSelectionChanged={handleSelectionChanged}
      keyExpr="id"
      {...dataGridConfig}
      height={360}
      onRowPrepared={onRowPrepared}
    >
      <Selection mode="multiple" showCheckBoxesMode="always" />
      <Column
        type="selection"
        caption="Select"
        width={70}
        headerCellRender={renderSelectionHeader}
      />
      <Column dataField="fileName" caption="Filename" />
      <Column dataField="folder" caption="Folder / Parent Folder" />
      <Column dataField="formattedSize" caption="Size" width={90} />
      <Column dataField="zip" caption="Zip" />
      <Column dataField="restoreStatus" caption="Restore status" />
      <Paging defaultPageSize={15} />
    </DataGrid>
  ), [
    filteredArchivedFiles,
    handleSelectionChanged,
    onRowPrepared,
    renderSelectionHeader,
  ]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      className="hq-modal cui-c_modal restore-archived-files-modal"
    >
      <DialogTitle className="cui-c_modal-title hq-title">
        Archived Files
        <IconButton
          aria-label="close"
          onClick={onClose}
          className="modal_button-cancel"
        >
          <CloseIcon sx={{ fontSize: 26 }} />
        </IconButton>
      </DialogTitle>
      <DialogContent className="cui-c_modal-body">
        <div className="restore-description">
          Select files you want to restore to the site from the backup.
        </div>

        <div className="restore-toolbar">
          <div className="selected-files-info">
            <span className="info-with-tooltip">
              Selected Files: {selectedFileInfo.count}
              <Tooltip
                title={`Maximum ${MAX_FILE_COUNT} files can be restored at a time.`}
                arrow
                placement="top"
              >
                <InfoOutlinedIcon className="info-icon" fontSize="small" />
              </Tooltip>
            </span>

            <span className="info-with-tooltip">
              Total Size of Selected Files: {selectedFileInfo.formattedSize}
              <Tooltip
                title="Maximum total restore size allowed is 2 GB."
                arrow
                placement="top"
              >
                <InfoOutlinedIcon className="info-icon" fontSize="small" />
              </Tooltip>
            </span>
          </div>

          <div className="restore-actions">
            <TextField
              size="small"
              variant="outlined"
              placeholder="Search File, Folder, or Zip"
              className="search-input"
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              // Attach ref to the input element so we can restore focus after clearing
              inputRef={(el) => (inputRef.current = el)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ fontSize: 18, color: "#757575" }} />
                  </InputAdornment>
                ),
                // Conditionally render clear button when there is any text
                endAdornment: (
                  <>
                    {searchText && searchText.length > 0 ? (
                      <InputAdornment position="end">
                        <IconButton
                          aria-label="clear search"
                          size="small"
                          onClick={handleClearSearch}
                          className="clear-search-button"
                        >
                          <CloseIcon />
                        </IconButton>
                      </InputAdornment>
                    ) : null}
                  </>
                ),
              }}
              sx={{
                "& .MuiInputAdornment-root": {
                  marginRight: "2px",
                },
                "& .MuiOutlinedInput-notchedOutline": {
                  border: "1px solid #c4c4c4 !important",
                },
                "& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline": {
                  border: "1px solid #c4c4c4 !important",
                },
                "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
                  border: "1px solid #009cde !important",
                },
              }}
            />
            <Button
              onClick={!isRestoring ? handleRestoreClick : undefined}
              variant="contained"
              color="primary"
              disableElevation
              disabled={!isRestoreEnabled}
              className={`hq_button restore-button ${
                isRestoring ? "restore-in-progress" : ""
              }`}
            >
              {isRestoring ? "Restoring..." : "Restore"}
            </Button>
          </div>
        </div>
        <div className="restore-grid-wrapper">
          {archivedFilesGrid}
        </div>
        <div className="total-files-info">
          Total files in archive: {archivedFiles.length}
        </div>
      </DialogContent>
      <DialogActions className="cui-c_modal-footer">
        <Button onClick={onClose} className="hq_button hq_button-secondary">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};
